import React, { useState } from "react";
import styled from "styled-components";
import { colors } from "utils";

const isObject = (v) => v !== null && typeof v === "object" && !Array.isArray(v);
const isContainer = (v) => isObject(v) || Array.isArray(v);

export const JsonTree = ({ data, defaultExpandDepth = 1 }) => (
  <Root>
    <Node value={data} depth={0} isLast defaultExpandDepth={defaultExpandDepth} />
  </Root>
);

const Node = ({ name, value, depth, isLast, defaultExpandDepth }) => {
  const container = isContainer(value);
  const [expanded, setExpanded] = useState(container && depth < defaultExpandDepth);

  const keyPart = name !== undefined ? <Key>&quot;{name}&quot;</Key> : null;
  const colon = name !== undefined ? <Punct>: </Punct> : null;
  const trailing = !isLast ? <Punct>,</Punct> : null;

  if (!container) {
    return (
      <Line $depth={depth}>
        <ArrowSlot />
        {keyPart}
        {colon}
        <PrimitiveValue value={value} />
        {trailing}
      </Line>
    );
  }

  const isArr = Array.isArray(value);
  const entries = isArr ? value.map((v, i) => [i, v]) : Object.entries(value);
  const open = isArr ? "[" : "{";
  const close = isArr ? "]" : "}";
  const summary = isArr
    ? `${value.length} item${value.length === 1 ? "" : "s"}`
    : `${entries.length} key${entries.length === 1 ? "" : "s"}`;

  return (
    <>
      <Line $depth={depth}>
        <Arrow
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-label={expanded ? "Collapse" : "Expand"}
        >
          {expanded ? "▾" : "▸"}
        </Arrow>
        {keyPart}
        {colon}
        <Punct>{open}</Punct>
        {!expanded && (
          <>
            <Summary> {summary} </Summary>
            <Punct>{close}</Punct>
            {trailing}
          </>
        )}
      </Line>
      {expanded && (
        <>
          {entries.map(([k, v], i) => (
            <Node
              key={k}
              name={isArr ? undefined : k}
              value={v}
              depth={depth + 1}
              isLast={i === entries.length - 1}
              defaultExpandDepth={defaultExpandDepth}
            />
          ))}
          <Line $depth={depth}>
            <ArrowSlot />
            <Punct>{close}</Punct>
            {trailing}
          </Line>
        </>
      )}
    </>
  );
};

const PrimitiveValue = ({ value }) => {
  if (value === null) return <NullValue>null</NullValue>;
  if (value === undefined) return <NullValue>undefined</NullValue>;
  if (typeof value === "string") return <StringValue>&quot;{value}&quot;</StringValue>;
  if (typeof value === "number") return <NumberValue>{value}</NumberValue>;
  if (typeof value === "boolean") return <BoolValue>{String(value)}</BoolValue>;
  return <Punct>{String(value)}</Punct>;
};

const Root = styled.div`
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px;
  line-height: 1.6;
  color: ${colors.nightlyWoods500};
`;

const Line = styled.div`
  padding-left: ${(p) => p.$depth * 16}px;
  white-space: pre-wrap;
  word-break: break-word;
`;

const Arrow = styled.button`
  display: inline-block;
  width: 14px;
  padding: 0;
  margin-right: 2px;
  border: none;
  background: none;
  color: ${colors.milkGlass950};
  cursor: pointer;
  user-select: none;
  font: inherit;
  line-height: 1;

  &:hover {
    color: ${colors.nightlyWoods500};
  }
`;

const ArrowSlot = styled.span`
  display: inline-block;
  width: 16px;
`;

const Key = styled.span`
  color: ${colors.blue500};
`;

const Punct = styled.span`
  color: ${colors.nightlyWoods500};
`;

const Summary = styled.span`
  color: ${colors.milkGlass950};
  font-style: italic;
`;

const StringValue = styled.span`
  color: ${colors.green500};
`;

const NumberValue = styled.span`
  color: ${colors.orange700};
`;

const BoolValue = styled.span`
  color: ${colors.pink500};
`;

const NullValue = styled.span`
  color: ${colors.milkGlass950};
  font-style: italic;
`;
